import { ActivityIndicator, View } from 'react-native';
import React, { useEffect, useState } from 'react';

import { BranchAddress } from '@stores/lookups/types';
import { EmptyContentPlaceholder } from '@components/EmptyContentPlaceholder';
import { ScreenContent } from '@components/ScreenContent';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useLookupsStore } from '@stores/lookups';
import { useMenusStore } from '@stores/menus';
import { useTranslation } from '@providers/I18n';

import { BranchesDropdown } from './BranchesDropdown';
import { MenusTabs } from './MenusTabs';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    padding: theme.spacing(2),
  },
  content: {
    height: '90%',
  },
  title: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    flexDirection: 'row',
    padding: theme.spacing(2),
    height: '95%',
  },
  tabs: {
    flex: 1,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
}));

export const MenuScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const { value } = useLookupsStore();
  const {
    menus,
    loading,
    branch: selectedBranch,
    fetch: fetchMenus,
    reset: resetMenus,
  } = useMenusStore();

  const [branches, setBranches] = useState<BranchAddress[]>([]);
  const lang = i18n.language;

  useEffect(() => {
    checkLookupsValue();
    return resetMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const checkLookupsValue = async () => {
    if (value) {
      await setBranches(value.branchesAddress);
      if (value.branchesAddress.length === 1) {
        await fetchMenus(value.branchesAddress[0]);
      }
    }
  };

  const filterBranches = (searchValue: string) => {
    if (lang === 'en') {
      searchValue = searchValue.toLocaleLowerCase();
    }
    const arr: BranchAddress[] = value
      ? value.branchesAddress.filter((branch) => {
          const branchAddress =
            lang === 'ar'
              ? branch.branchAddress.arName
              : branch.branchAddress.enName.toLocaleLowerCase();
          const restaurantName =
            lang === 'ar'
              ? branch.restaurantName.arName
              : branch.restaurantName.enName.toLocaleLowerCase();
          return (
            branchAddress.includes(searchValue) ||
            restaurantName.includes(searchValue)
          );
        })
      : [];
    setBranches(arr);
  };

  const fetchBranchMenus = async (branch: BranchAddress) => {
    if (branch.branchUUID !== selectedBranch?.branchUUID) {
      await fetchMenus(branch);
    }
  };
  return (
    <ScreenContent style={styles.root}>
      <Typography size="3xl" weight="semiBold" style={styles.title}>
        {t('menu.title.myMenu', 'My Menu')}
      </Typography>
      <View style={styles.content}>
        <BranchesDropdown
          branches={branches}
          loading={loading}
          selectedBranch={selectedBranch}
          fetchMenus={fetchBranchMenus}
          filterBranches={filterBranches}
        />
        {loading && (
          <View style={styles.loading}>
            <ActivityIndicator size="large" />
          </View>
        )}
        {!selectedBranch && !loading && (
          <EmptyContentPlaceholder
            message={t(
              'menu.noSelectedMenuMessage',
              'Select a branch to view its menu(s)',
            )}
          />
        )}
        {menus.length > 0 && !loading && selectedBranch && (
          <View>
            <MenusTabs
              tabs={menus}
              selectedBranchUUID={selectedBranch?.branchUUID}
            />
          </View>
        )}
      </View>
    </ScreenContent>
  );
};
